import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { availableLanguages, type Language, type Translations, translations } from "@web/lib/i18n"
import { api } from "../axios"

interface LanguageContextType {
  language: Language
  setLanguage: (language: Language) => void
  t: Translations
  availableLanguages: typeof availableLanguages
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const LANGUAGE_STORAGE_KEY = "language"
const LANGUAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000

interface StoredLanguage {
  value: Language
  savedAt: number
}

const readStoredLanguage = (): Language | null => {
  const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (!raw) return null

  let stored: Partial<StoredLanguage>
  try {
    stored = JSON.parse(raw)
  } catch {
    return null
  }

  const isValidLanguage = availableLanguages.some((lang) => lang.code === stored.value)
  if (!isValidLanguage || typeof stored.savedAt !== "number") return null

  const isExpired = Date.now() - stored.savedAt > LANGUAGE_TTL_MS
  if (isExpired) return null

  return stored.value as Language
}

const writeStoredLanguage = (language: Language) => {
  const stored: StoredLanguage = { value: language, savedAt: Date.now() }
  localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(stored))
}

const detectBrowserLanguage = (): Language => {
  const browserLanguages = navigator.languages ?? [navigator.language]
  for (const browserLang of browserLanguages) {
    const primaryTag = browserLang.split("-")[0].toLowerCase()
    const match = availableLanguages.find((lang) => lang.code === primaryTag)
    if (match) return match.code
  }
  return "pt"
}

const getInitialLanguage = (defaultLanguage?: Language): Language => {
  return readStoredLanguage() ?? defaultLanguage ?? detectBrowserLanguage()
}

export const initializeLanguage = (defaultLanguage?: Language): Language => {
  const language = getInitialLanguage(defaultLanguage)
  writeStoredLanguage(language)
  return language
}

export function LanguageProvider({ children, defaultLanguage }: { children: React.ReactNode; defaultLanguage?: Language }) {
  const [language, setLanguage] = useState<Language>(() => getInitialLanguage(defaultLanguage))
  const changeLanguage = (nextLanguage: Language) => {
    writeStoredLanguage(nextLanguage)
    setLanguage(nextLanguage)
  }

  useEffect(() => {
    api.defaults.headers.common["Accept-Language"] = language
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    writeStoredLanguage(language)
  }, [language])

  const value = {
    language,
    setLanguage: changeLanguage,
    t: translations[language],
    availableLanguages,
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
